import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { route } from "@routes/helper";
import moment from "moment";

import { REPORTING_EMPLOYEE_ABSENCE_DETAILS_PATH } from "@routes/constants";
import actionCreators from "@modules/service/actionCreator";
import type { State } from "@store/reducers";
import type {
  AbsenceAccessAuditLog,
  AuditLogsFilterType,
} from "@modules/service/reducer/audit-logs/types";
import { type PaginationExtendedType } from "@types";
import { Button, Pagination, Loader, GSGrid, View, FormGroup } from "@components";
import { AuditLogsFilter } from "@modules/shared/components";
import * as serviceSelectors from "@modules/service/selectors";

type PropsFromState = {
  isLoading: boolean,
  list: Array<AbsenceAccessAuditLog>,
  pagination: PaginationExtendedType,
};

type PropsFromDispatch = {
  getAbsenceAccessAuditLogs: typeof actionCreators.GetAbsenceAccessAuditLogs,
  exportAbsenceAccessAuditLogs: typeof actionCreators.ExportAbsenceAccessAuditLogs,
  getAuditLogsReset: typeof actionCreators.GetAuditLogsReset,
};

type Props = {
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  clientId: number,
};

export function AbsenceAccessAuditLogs(props: Props) {
  const { list, pagination, clientId, t } = props;
  const [searchParams, setSearchParams]: [?AuditLogsFilterType, Function] = useState({
    clientId,
    search: {
      dateFrom: null,
      dateTo: null,
      userId: null,
      employeeId: null,
      absenceId: null,
    },
    page: 1,
  });

  useEffect(() => {
    props.getAuditLogsReset();
  }, []);
  const columns = [
    {
      name: t("USER_ID"),
      key: "userId",
      align: "left",
      width: "fit-content",
      renderCell: ({ row }: Object) => row.userId,
    },
    {
      name: t("USER_NAME"),
      align: "left",
      key: "userFullName",
    },
    {
      name: t("ACCESS_DATE"),
      key: "accessDate",
      width: "fit-content",
      renderCell: ({ row }: Object) => GSGrid.render.date(row.accessDate),
    },
    {
      name: t("EMPLOYEE_ID"),
      key: "employeeId",
      align: "left",
      width: "fit-content",
    },
    {
      name: t("EMPLOYEE_RECORD"),
      key: "employeeFullName",
      align: "left",
    },
    {
      name: t("ABSENCE_ID"),
      key: "absenceId",
      width: "fit-content",
    },
    {
      name: t("ABSENCE_START_DATE"),
      key: "absenceStartDate",
      width: "fit-content",
      renderCell: ({ row }: Object) => GSGrid.render.date(row.absenceStartDate),
    },
    {
      name: t("VIEW"),
      key: props.t("VIEW"),
      width: 40,
      align: "center",
      onCellClick: (row: Object) =>
        window.open(
          route("REPORTING_EMPLOYEE_ABSENCE_DETAILS_PATH", {
            clientId,
            employeeId: row.employeeId,
            absenceId: row.absenceId,
          }),
          "_blank",
        ),
      renderCell: () => GSGrid.render.materialIcon("play_circle"),
    },
  ];

  function handleSubmitFilter() {
    props.getAbsenceAccessAuditLogs({ ...searchParams, page: 1 });
  }

  function handleFilterChange(data) {
    setSearchParams({
      ...searchParams,
      search: {
        ...data,
        dateFrom: moment(data.dateFrom)
          .set("hour", 0)
          .set("minute", 0)
          .set("second", 0)
          .utc()
          .toISOString(),
        dateTo: moment(data.dateTo)
          .set("hour", 23)
          .set("minute", 59)
          .set("second", 59)
          .utc()
          .toISOString(),
      },
    });
  }

  function handleExport(e: Event) {
    e.preventDefault();
    props.exportAbsenceAccessAuditLogs(searchParams);
  }

  function handleChangePage(page: string) {
    const res = {
      ...searchParams,
      page: parseInt(page),
    };

    setSearchParams(res);
    props.getAbsenceAccessAuditLogs(res);
  }

  const isValid = !!(
    searchParams.search.absenceId ||
    searchParams.search.employeeId ||
    searchParams.search.userId
  );

  return (
    <View>
      {props.isLoading && <Loader />}
      <View className="message-box" margin={[0, 0, "2rem", 0]}>
        {props.t("ABSENCE_ACCESS_AUDITS_INFORMATION")}
      </View>
      <View gap={4} className="gs-container">
        <View justifyContent="space-between" direction="row" margin={[0, 0, "1rem", 0]}>
          <View>
            <AuditLogsFilter
              handleSubmit={handleSubmitFilter}
              onChange={handleFilterChange}
              data={searchParams.search}
              showAbsenceId
              isValid={isValid}
              requiredFields={isValid ? [] : ["userId", "employeeId", "absenceId"]}
            />
          </View>
          <View direction="row" justifyContent="center" alignItems="flex-end">
            <FormGroup>
              <Button materialIcon="file_download" onClick={handleExport} disabled={!list.length}>
                {t("EXPORT")}
              </Button>
            </FormGroup>
          </View>
        </View>

        <GSGrid columns={columns} rows={list} />

        {pagination && (
          <Pagination
            className="my-3 d-flex flex-fill justify-content-center"
            currentPage={pagination.pageNumber}
            totalPages={pagination.totalPages}
            onChange={handleChangePage}
          />
        )}
      </View>
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    list: serviceSelectors.selectAbsenceAccessAuditLogsList(state),
    pagination: serviceSelectors.selectAbsenceAccessAuditLogsPagination(state),
    isLoading: serviceSelectors.isLoadingAuditLogs(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getAbsenceAccessAuditLogs: actionCreators.GetAbsenceAccessAuditLogs,
  exportAbsenceAccessAuditLogs: actionCreators.ExportAbsenceAccessAuditLogs,
  getAuditLogsReset: actionCreators.GetAuditLogsReset,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceAccessAuditLogs);
